import { Typography } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import React, { useMemo } from "react";
import { Provider } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import store from "./model/store";
import defaultTheme from "./themes";
import { useAuthenticate, useBroadcast } from "./utils/customHooks";

import "ol/ol.css";
import "./App.scss";
import "./Globals.module.scss";

function AppChildren({
  children,
  history,
  location,
  params,
}: {
  children: React.ReactNode;
  history: {
    // eslint-disable-next-line no-unused-vars
    push: (path: string) => void;
    // eslint-disable-next-line no-unused-vars
    replace: (path: string) => void;
  };
  location: {
    pathname: string;
  };
  params: Record<string, unknown>;
}) {
  const { isAuthComplete } = useAuthenticate();
  useBroadcast();

  if (!isAuthComplete) {
    // Only load the app once the authentication process is complete
    return <Typography>Redirecting...</Typography>;
  }

  return React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { history, location, params });
    }
    return child;
  });
}

function App({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const history = useMemo(() => {
    return {
      push: (path) => {
        navigate(path);
      },
      replace: (path) => {
        navigate(path);
      },
    };
  }, [navigate]);

  return (
    <Provider store={store}>
      <ThemeProvider theme={defaultTheme}>
        <AppChildren history={history} location={location} params={params}>
          {children}
        </AppChildren>
      </ThemeProvider>
    </Provider>
  );
}

export default React.memo(App);
